import React, { FunctionComponent, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RestoreIcon from "@mui/icons-material/Restore";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Button, Stack } from "@mui/material";
import {
  downloadUtilityBillPdf,
  deletePurchaseDocument,
  RENEWABLE_ENERGY_PURCHASES_API_PATHS,
} from "api";
import { COLORS } from "components/consts";
import { DeleteDialog, useDialogControls } from "components/dialog";
import { useNotify } from "hooks";
import { MaybeNull } from "types";
import { downloadFile, openPdfInNewTab } from "utils";

interface PurchaseActionsProps {
  documentId: MaybeNull<number>;
  actionsDisabled: boolean;
  downloadUrl: MaybeNull<string>;
  fileName?: MaybeNull<string>;
  setScale: React.Dispatch<React.SetStateAction<number>>;
  handleFileName: any;
}

const ZOOM = 0.25;

const iconWrapperStyle = {
  padding: 0,
  minWidth: "fit-content",
  color: COLORS.white,
};

const deleteIconWrapperStyle = {
  padding: 0,
  minWidth: "fit-content",
  color: COLORS.white,
  marginLeft: "22px",
};

export const PurchaseActions: FunctionComponent<PurchaseActionsProps> = ({
  documentId,
  actionsDisabled,
  downloadUrl,
  fileName,
  setScale,
  handleFileName,
}) => {
  const fileInputRef: any = useRef(null);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const deleteDialogControls: any = useDialogControls();

  const { mutateAsync: deletePurchaseDocumentRequest } = useMutation(
    deletePurchaseDocument,
  );

  const deleteDocument = async () => {
    await deletePurchaseDocumentRequest(documentId!, {
      onError: () => {
        notify.error(`Some error has happened while deleting document!`);
      },
      onSuccess: () => {
        queryClient.invalidateQueries([
          RENEWABLE_ENERGY_PURCHASES_API_PATHS.RENEWABLE_ENERGY_PURCHASES,
        ]);
      },
    });
  };

  const handleFileChange = (e: any) => {
    const files = e.target.files[0];
    handleFileName(files);
  };

  const handleIconClick = () => {
    if (fileInputRef.current && documentId !== null) {
      fileInputRef.current.click();
    } else {
      notify.error(
        "You have to add data to purchase and save it before add the document",
      );
    }
  };

  const handleDeleteClick = () => {
    if (downloadUrl !== null) {
      deleteDocument();
      deleteDialogControls.close();
      fileInputRef.current.value = null;
      handleFileName(null);
      notify.success("Document deleted!");
    } else {
      notify.error("There is no document to delete!");
      deleteDialogControls.close();
    }
  };

  return (
    <Stack direction="row" spacing={4}>
      <Button
        disabled={actionsDisabled}
        onClick={
          downloadUrl === null
            ? () => notify.error("There is no file to download")
            : () =>
                downloadFile(
                  () => downloadUtilityBillPdf(downloadUrl),
                  fileName || "renewable-energy-purchase.pdf",
                )
        }
        sx={iconWrapperStyle}
        variant="text"
      >
        <CloudDownloadIcon />
      </Button>

      <Button
        disabled={actionsDisabled}
        onClick={() => {
          setScale((state) => state + ZOOM);
        }}
        sx={iconWrapperStyle}
        variant="text"
      >
        <ZoomInIcon />
      </Button>
      <Button
        disabled={actionsDisabled}
        onClick={() => {
          setScale((state) => state - ZOOM);
        }}
        sx={iconWrapperStyle}
        variant="text"
      >
        <ZoomOutIcon />
      </Button>
      <Button
        disabled={actionsDisabled}
        onClick={() => setScale(0.75)}
        sx={iconWrapperStyle}
        variant="text"
      >
        <RestoreIcon />
      </Button>

      <Button
        disabled={actionsDisabled}
        onClick={
          downloadUrl === null
            ? () => notify.error("There is no file to open")
            : () => openPdfInNewTab(() => downloadUtilityBillPdf(downloadUrl))
        }
        sx={{ marginLeft: "22px", ...iconWrapperStyle }}
        variant="text"
      >
        <OpenInNewIcon />
      </Button>

      <Box sx={{ display: "flex", alignItems: "center" }}>
        <UploadFileIcon
          onClick={handleIconClick}
          style={{ cursor: "pointer" }}
        />
        <input
          type="file"
          accept=".pdf"
          style={{ display: "none" }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
      </Box>

      <Button
        disabled={actionsDisabled}
        onClick={deleteDialogControls.open}
        sx={deleteIconWrapperStyle}
        variant="text"
      >
        <DeleteForeverIcon />
      </Button>

      <DeleteDialog
        isOpen={deleteDialogControls.isOpen}
        onCancel={deleteDialogControls.close}
        onDeleteClick={handleDeleteClick}
        target="Purchase"
        title="Delete Purchase document?"
      />
    </Stack>
  );
};
